import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Chip, Icon, Select, Toast } from "@legal-500/legal500-design-system";
import type { Column } from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { PaginationControls } from "@/components/PaginationControls";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import { TaxonomyToggle } from "@/components/TaxonomyToggle";
import { useQuery, useMutation } from "@/hooks/useApi";
import { useNewAxisEditions } from "@/hooks/useNewAxisEditions";
import { usePublications } from "@/hooks/usePublications";
import { TAXONOMY_URL_PARAM, useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import { useUrlParams } from "@/hooks/useUrlParams";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import type { TaxonomyMode } from "@/api/submissions.service";
import {
  surveysService,
  type Survey,
  type SurveyStatus,
  type CreateSurveyBody,
} from "@/api/surveys.service";
import { newAxisEditionOptions } from "@/api/surveyTaxonomy.service";
import { taxonomyFilterParams } from "@/utils/taxonomyFilter";
import { SurveyFormModal } from "./SurveyFormModal";
import {
  isDuplicateSurveyConflict,
  duplicateSurveyConflictMessage,
  isSurveyDeleteConflict,
  surveyDeleteBlockedMessage,
} from "./surveyErrors";
import { SurveyConditionChips } from "../surveyConditions";
import { ROUTES } from "@/routes";
import styles from "./SurveyManagement.module.css";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
];

const STATUS_CHIP_COLOR: Record<SurveyStatus, "success" | "default"> = {
  published: "success",
  draft: "default",
};

const DEFAULT_ITEMS_PER_PAGE = 10;

function SurveyManagementPage() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const { updateUrl } = useUrlParams();
  const { toggleVisible, mode, taxonomy, ready } = useTaxonomyMode();
  const [editionFilter, setEditionFilter] = useState("");
  const [publicationFilter, setPublicationFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deletingSurvey, setDeletingSurvey] = useState<Survey | null>(null);
  const [cloningSurvey, setCloningSurvey] = useState<Survey | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  const editionOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [{ value: "", label: "All Editions" }];
    for (let year = currentYear + 1; year >= 2017; year--) {
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);

  const { data: publicationsData } = usePublications(
    { edition: editionFilter || undefined },
    { enabled: ready && mode === "legacy" },
  );

  const { data: newAxisEditions } = useNewAxisEditions({ enabled: ready && mode === "new" });

  const publicationOptions = useMemo(() => {
    if (mode === "new") {
      return [
        { value: "", label: "All Publications" },
        ...newAxisEditionOptions(newAxisEditions, editionFilter),
      ];
    }
    const pubs = publicationsData?.publications ?? [];
    return [
      { value: "", label: "All Publications" },
      ...pubs.map((p: LegacyPublication) => ({
        value: String(p.id),
        label: p.publication ? `${p.publication} (${p.edition ?? "n/a"})` : `Publication ${p.id}`,
      })),
    ];
  }, [mode, newAxisEditions, editionFilter, publicationsData?.publications]);

  const { publicationId, editionId } = taxonomyFilterParams("publication", publicationFilter, mode);

  const offset = (currentPage - 1) * itemsPerPage;

  const {
    data: listResponse,
    loading,
    error,
    refetch,
    isRefetching,
  } = useQuery(
    () =>
      ready
        ? surveysService.list({
            edition: editionFilter || undefined,
            publicationId,
            editionId,
            taxonomy,
            status: (statusFilter as SurveyStatus) || undefined,
            sortField: (sortField as "name" | "publication" | "edition" | "status") || undefined,
            sortDirection: sortField ? sortDirection : undefined,
            limit: itemsPerPage,
            offset,
          })
        : Promise.resolve(null),
    [
      ready,
      editionFilter,
      publicationId,
      editionId,
      taxonomy,
      statusFilter,
      currentPage,
      itemsPerPage,
      sortField,
      sortDirection,
    ],
  );

  const sortableColumnKeys = ["name", "publication", "edition", "status"];

  const handleSort = useCallback(
    (field: string) => {
      if (field !== sortField) {
        setSortField(field);
        setSortDirection("asc");
        setCurrentPage(1);
      } else if (sortDirection === "asc") {
        setSortDirection("desc");
        setCurrentPage(1);
      } else {
        setSortField(null);
        setSortDirection("asc");
        setCurrentPage(1);
      }
    },
    [sortField, sortDirection],
  );

  const { mutate: createSurvey } = useMutation((data: CreateSurveyBody) =>
    surveysService.create(data),
  );

  const { mutate: deleteSurvey } = useMutation((id: string) => surveysService.remove(id));

  const surveys = listResponse?.surveys ?? [];
  const totalCount = listResponse?.total ?? 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleAddClick = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleSave = useCallback(
    async (data: CreateSurveyBody) => {
      try {
        const response = await createSurvey(data);
        await refetch();
        setToast({ message: "Survey created successfully.", type: "success" });
        navigate(ROUTES.surveys.detail(response.survey.id));
      } catch (error) {
        if (isDuplicateSurveyConflict(error)) {
          setToast({ message: duplicateSurveyConflictMessage(error), type: "error" });
        } else {
          const message =
            error instanceof Error ? error.message : "Failed to save survey. Please try again.";
          setToast({ message, type: "error" });
        }
        throw error;
      }
    },
    [createSurvey, refetch, navigate],
  );

  const handleCloneSave = useCallback(
    async (data: CreateSurveyBody) => {
      const sourceName = cloningSurvey?.name.trim() ?? "";
      let name = data.name.trim();
      if (sourceName.length > 0 && name === sourceName) {
        name = `${sourceName} (Copy)`;
      }
      const payload: CreateSurveyBody = { ...data, name };
      try {
        const response = await createSurvey(payload);
        await refetch();
        setToast({ message: "Survey copy created successfully.", type: "success" });
        navigate(ROUTES.surveys.detail(response.survey.id));
      } catch (error) {
        if (isDuplicateSurveyConflict(error)) {
          setToast({ message: duplicateSurveyConflictMessage(error), type: "error" });
        } else {
          const message =
            error instanceof Error ? error.message : "Failed to save survey. Please try again.";
          setToast({ message, type: "error" });
        }
        throw error;
      }
    },
    [createSurvey, refetch, navigate, cloningSurvey?.name],
  );

  const handleDeleteClick = useCallback((survey: Survey) => {
    setDeletingSurvey(survey);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingSurvey) return;
    try {
      await deleteSurvey(deletingSurvey.id);
      setDeletingSurvey(null);
      setToast({ message: "Survey deleted.", type: "success" });
      refetch();
    } catch (error) {
      if (isSurveyDeleteConflict(error)) {
        // A 409 is a deliberate guard (the survey already has referee responses).
        // Rethrow a readable error so ConfirmationModal renders it inline and
        // stays open, instead of a transient toast that reads like a failure.
        throw new Error(surveyDeleteBlockedMessage(error));
      }
      const message =
        error instanceof Error ? error.message : "Failed to delete survey. Please try again.";
      setToast({ message, type: "error" });
    }
  }, [deletingSurvey, deleteSurvey, refetch]);

  const handleCloneClick = useCallback((survey: Survey) => {
    setCloningSurvey(survey);
  }, []);

  const handleCreateClose = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleCloneClose = useCallback(() => {
    setCloningSurvey(null);
  }, []);

  const handleEditionFilterChange = (value: string) => {
    setEditionFilter(value);
    setPublicationFilter("");
    setCurrentPage(1);
  };

  const handlePublicationFilterChange = (value: string) => {
    setPublicationFilter(value);
    setCurrentPage(1);
  };

  const handleTaxonomyChange = (next: TaxonomyMode) => {
    setPublicationFilter("");
    setCurrentPage(1);
    updateUrl({ [TAXONOMY_URL_PARAM]: next });
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const columns: Column<Survey>[] = useMemo(
    () => [
      {
        header: "Name",
        accessor: "name" as keyof Survey,
        render: (_value: unknown, row: Survey) => (
          <span className={styles.nameText}>{row.name}</span>
        ),
      },
      {
        header: "Publication",
        accessor: "publication" as keyof Survey,
      },
      {
        header: "Edition",
        accessor: "edition" as keyof Survey,
      },
      {
        header: "Conditions",
        accessor: "id" as keyof Survey,
        render: (_value: unknown, row: Survey) => <SurveyConditionChips conditions={row} />,
      },
      {
        header: "Status",
        accessor: "status" as keyof Survey,
        render: (value: unknown) => {
          const status = value as SurveyStatus;
          return (
            <Chip
              size="small"
              color={STATUS_CHIP_COLOR[status]}
              className={status === "draft" ? styles.draftStatusChip : undefined}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Chip>
          );
        },
      },
      {
        header: "Version",
        accessor: "version" as keyof Survey,
        render: (_value: unknown, row: Survey) => (row.status === "draft" ? "-" : row.version),
      },
      {
        header: "Def. language",
        accessor: "defaultLanguage" as keyof Survey,
        render: (value: unknown) => <span>{String(value).toUpperCase()}</span>,
      },
      {
        header: "Actions",
        accessor: "id" as keyof Survey,
        render: (_value: unknown, row: Survey) => (
          <div className={styles.actionsCell}>
            {(!row.editionId || toggleVisible) && (
              <Button
                variant="outlined"
                size="small"
                aria-label="Clone survey"
                onClick={event => {
                  event.stopPropagation();
                  handleCloneClick(row);
                }}
              >
                <Icon icon="copy" size="small" />
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              size="small"
              aria-label="Delete survey"
              onClick={event => {
                event.stopPropagation();
                handleDeleteClick(row);
              }}
            >
              <Icon icon="delete" size="small" />
            </Button>
          </div>
        ),
      },
    ],
    [handleCloneClick, handleDeleteClick, toggleVisible],
  );

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Surveys"
        subtitle="Manage survey templates linked to editions and publications"
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddClick}
          startIcon={<Icon icon="plus" size="small" />}
        >
          Create New
        </Button>
      </PageHeader>

      {toggleVisible && <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} />}

      <div className={styles.filterRow}>
        <div className={styles.filters}>
          <div className={styles.filterField}>
            <Select
              label="Edition"
              value={editionFilter}
              onChange={e => handleEditionFilterChange(e.target.value)}
              options={editionOptions}
              size="small"
            />
          </div>
          <div className={styles.filterField}>
            <Select
              label="Publication"
              value={publicationFilter}
              onChange={e => handlePublicationFilterChange(e.target.value)}
              options={publicationOptions}
              size="small"
            />
          </div>
          <div className={styles.filterField}>
            <Select
              label="Status"
              value={statusFilter}
              onChange={e => handleStatusFilterChange(e.target.value)}
              options={STATUS_OPTIONS}
              size="small"
            />
          </div>
        </div>
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="itemsPerPage" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <Select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={e => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      {!ready || (loading && listResponse === null) ? (
        <LoadingState message="Loading surveys..." />
      ) : !error && surveys.length === 0 ? (
        <EmptyState
          variant="empty"
          title="No surveys found"
          message="No surveys match your filter criteria. Try adjusting your filters or create a new survey."
        />
      ) : (
        !error && (
          <>
            <div className={styles.tableContainer}>
              <div className={styles.scopedTable}>
                <DataTable
                  data={surveys}
                  columns={columns}
                  onRowClick={row => navigate(ROUTES.surveys.detail(row.id))}
                  emptyMessage="No surveys found matching your criteria."
                  sortableColumnKeys={sortableColumnKeys}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
              </div>
            </div>

            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + surveys.length}
              totalCount={totalCount}
              itemName="surveys"
              onPageChange={setCurrentPage}
            />
          </>
        )
      )}

      <SurveyFormModal isOpen={isCreateModalOpen} onClose={handleCreateClose} onSave={handleSave} />

      {cloningSurvey && (
        <SurveyFormModal
          isOpen={!!cloningSurvey}
          onClose={handleCloneClose}
          onSave={handleCloneSave}
          title={`Clone: ${cloningSurvey.name}`}
          submitLabel="Clone"
          taxonomy={cloningSurvey.editionId ? "new" : "legacy"}
          initialValues={{
            name: cloningSurvey.name,
            edition: cloningSurvey.edition,
            publication: cloningSurvey.publication,
            publicationId: cloningSurvey.publicationId,
            editionId: cloningSurvey.editionId,
            defaultLanguage: cloningSurvey.defaultLanguage,
            definition: cloningSurvey.definition,
            randomQuestionCount: cloningSurvey.randomQuestionCount,
            regionTypes: cloningSurvey.regionTypes,
            refereeTypes: cloningSurvey.refereeTypes,
            refereeCategories: cloningSurvey.refereeCategories,
          }}
        />
      )}

      {deletingSurvey && (
        <ConfirmationModal
          size="small"
          title="Delete Survey"
          message="Are you sure you want to delete this survey?"
          warningMessage="This action cannot be undone. The survey will be permanently removed."
          backdropClick={false}
          affectedItems={[{ id: deletingSurvey.id, name: deletingSurvey.name }]}
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setDeletingSurvey(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default SurveyManagementPage;
